import { useMemo } from "react";

import { PurchaseOrderDetailLink, SalesOrderDetailLink, WorkOrderDetailLink } from "@/components/hub/HubEntityDetailLink";
import { MrpSkuHoverPeek, useDeferredMrpSkuHoverHints } from "@/components/mrp/MrpSkuHoverPeek";
import type { MrpInventoryOutgoingWorkOrderLine } from "@/lib/mrp";
import { cn, EM_DASH } from "@/lib/utils";

import {
  formatInventoryHoverPromiseDate,
  inventoryWorkOrderHoverLineKey,
  inventoryWorkOrderReferenceKind,
  inventoryWorkOrderReferencePromiseDate,
} from "./skuDetailInventoryWorkOrderHoverModel";

const inventoryHoverTableClass = "w-full border-collapse text-left text-xs";
const inventoryHoverThClass = "px-2 py-1 font-medium text-slate-500";
const inventoryHoverTdClass = "px-2 py-1 align-top text-slate-800";
const inventoryHoverTdMonoClass = "px-2 py-1 align-top font-mono text-[11px] text-slate-800";

function InventoryHoverPromiseDate({ isoDay }: { isoDay: string | null | undefined }): JSX.Element {
  const formatted = formatInventoryHoverPromiseDate(isoDay);
  if (formatted === "") {
    return <span className="text-slate-500">{EM_DASH}</span>;
  }
  return <span className="whitespace-nowrap tabular-nums">{formatted}</span>;
}

function InventoryWorkOrderNumberCell({ line }: { line: MrpInventoryOutgoingWorkOrderLine }): JSX.Element {
  if (line.build_order_id != null && line.build_order_id > 0) {
    return (
      <WorkOrderDetailLink
        buildOrderId={line.build_order_id}
        workOrderNumber={line.work_order_no}
        className="whitespace-nowrap text-[11px]"
      />
    );
  }
  return <span className="whitespace-nowrap">{line.work_order_no}</span>;
}

function InventoryWorkOrderReferenceCell({ line }: { line: MrpInventoryOutgoingWorkOrderLine }): JSX.Element {
  const reference = line.reference?.trim() ?? "";
  if (reference === "") {
    return <span className="text-slate-500">{EM_DASH}</span>;
  }
  const kind = inventoryWorkOrderReferenceKind(reference);
  if (kind === "gso" && line.sales_order_id != null && line.sales_order_id > 0) {
    return (
      <SalesOrderDetailLink
        salesOrderId={line.sales_order_id}
        salesOrderNumber={reference}
        className="whitespace-nowrap text-[11px]"
        peek={{
          sales_order_number: reference,
          client_name: line.sales_order_client_name,
          end_user_name: line.sales_order_end_user_name,
          customer_purchase_order_number: line.sales_order_customer_po,
          sgs_ship_by_date: line.sales_order_ship_date,
        }}
      />
    );
  }
  if (kind === "gpo" && line.purchase_order_id != null && line.purchase_order_id > 0) {
    return (
      <PurchaseOrderDetailLink
        purchaseOrderId={line.purchase_order_id}
        purchaseOrderNumber={reference}
        className="whitespace-nowrap text-[11px]"
        peek={{
          purchase_order_number: reference,
          promise_date: line.purchase_order_promise_date,
        }}
      />
    );
  }
  return <span className="whitespace-nowrap">{reference}</span>;
}

function InventoryWorkOrderFinishedGoodCell({
  sku,
  skuHoverHints,
  hintsLoading,
  enableHintsFetch,
}: {
  sku: string;
  skuHoverHints: ReturnType<typeof useDeferredMrpSkuHoverHints>["skuHoverHints"];
  hintsLoading: boolean;
  enableHintsFetch: () => void;
}): JSX.Element {
  const trimmed = sku.trim();
  if (trimmed === "") {
    return <span className="text-slate-500">{EM_DASH}</span>;
  }
  return (
    <MrpSkuHoverPeek
      sku={trimmed}
      hint={skuHoverHints?.[trimmed]}
      hintsLoading={hintsLoading}
      onHoverIntent={enableHintsFetch}
      tab="inventory"
      linkClassName="text-[11px]"
    />
  );
}

export function InventoryWorkOrderLinesHoverTable({
  lines,
}: {
  lines: MrpInventoryOutgoingWorkOrderLine[];
}): JSX.Element {
  const finishedGoodSkus = useMemo(
    () => lines.map((line) => line.finished_good_sku),
    [lines],
  );
  const { skuHoverHints, hintsLoading, enableHintsFetch } = useDeferredMrpSkuHoverHints(
    finishedGoodSkus,
    "sku-detail-inventory-work-order-hover",
  );

  return (
    <table className={cn(inventoryHoverTableClass, "table-fixed min-w-[40rem]")}>
      <colgroup>
        <col className="w-[4.5rem]" />
        <col className="w-[6.5rem]" />
        <col className="w-[6rem]" />
        <col className="w-[9rem]" />
        <col className="w-[7rem]" />
        <col className="w-[6rem]" />
      </colgroup>
      <thead>
        <tr className="border-b border-slate-200">
          <th className={cn(inventoryHoverThClass, "text-right")}>Qty</th>
          <th className={inventoryHoverThClass}>Work order</th>
          <th className={inventoryHoverThClass}>WO promise</th>
          <th className={inventoryHoverThClass}>Finished good</th>
          <th className={inventoryHoverThClass}>Reference</th>
          <th className={inventoryHoverThClass}>Ref promise</th>
        </tr>
      </thead>
      <tbody>
        {lines.map((line) => (
          <tr key={inventoryWorkOrderHoverLineKey(line)}>
            <td className={cn(inventoryHoverTdClass, "text-right tabular-nums font-medium")}>
              {line.quantity.toLocaleString()}
            </td>
            <td className={cn(inventoryHoverTdClass, "whitespace-nowrap")}>
              <InventoryWorkOrderNumberCell line={line} />
            </td>
            <td className={cn(inventoryHoverTdClass, "text-slate-600")}>
              <InventoryHoverPromiseDate isoDay={line.work_order_promise_date} />
            </td>
            <td className={inventoryHoverTdMonoClass}>
              <InventoryWorkOrderFinishedGoodCell
                sku={line.finished_good_sku}
                skuHoverHints={skuHoverHints}
                hintsLoading={hintsLoading}
                enableHintsFetch={enableHintsFetch}
              />
            </td>
            <td className={cn(inventoryHoverTdClass, "whitespace-nowrap text-slate-600")}>
              <InventoryWorkOrderReferenceCell line={line} />
            </td>
            <td className={cn(inventoryHoverTdClass, "text-slate-600")}>
              <InventoryHoverPromiseDate isoDay={inventoryWorkOrderReferencePromiseDate(line)} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
